import { AttachmentFile } from '@billy/design-system'
import { useCallback } from 'react'

import { useAccounts } from '@modules-deprecated/app/accounts'
import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ChartOfAccountId } from '@modules-deprecated/app/organization/enums/chartOfAccountId'
import { useTaxRates } from '@modules-deprecated/app/taxrates'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { Scope } from '../../../../../enums/scope'
import { useFormContext } from '../../../../../hooks/useForm'
import { getCurrentLocale } from '../../../../../utils'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { autoCompleteBillForm } from '../utils/autoCompleteBillForm'
import { BillForm, BillFormSchema } from '../utils/formData'
import { getBillFormFromAttachmentData } from '../utils/getBillFormFromAttachmentData'
import { getBillFormFromVendorData } from '../utils/getBillFormFromVendorData'
import { resetAutoCompletedBillForm } from '../utils/resetAutoCompletedBillForm'

interface GetAutocompletedFormWithAttachmentDataProps {
  accounts?: Account[]
  attachmentFile?: AttachmentFile
  baseCurrencyId?: string
  billForm: BillForm
  chartOfAccountId?: ChartOfAccountId
  countryId?: string
  locale: string
  organizationId: string
  taxRates?: TaxRate[]
}

export const getAutoCompletedFormWithAttachmentData = async ({
  accounts,
  attachmentFile,
  baseCurrencyId,
  billForm,
  chartOfAccountId,
  countryId,
  locale,
  organizationId,
  taxRates,
}: GetAutocompletedFormWithAttachmentDataProps): Promise<BillForm> => {
  const isVoucherScanningEnabled = isAuthorized(Scope.VoucherScanRead)

  if (!isVoucherScanningEnabled) {
    return billForm
  }

  const billFormAttachmentData = await getBillFormFromAttachmentData({
    attachmentFile,
    billForm,
    chartOfAccountId,
    countryId,
    locale,
    organizationId,
    taxRates,
    withBookkeepingSuggestions: true,
  })

  let billFormUpdated = autoCompleteBillForm({
    // clean up autocompletion flags from previous sources
    // ensures correct coloring of fields that are completed by Bohr and VAT refresh
    billFormDefault: { ...billForm, autoCompletedFields: {} },
    billFormOverrides: billFormAttachmentData,
    baseCurrencyId,
    onlyFirstBillLine: false,
  })

  /* If new vendor data present, we want to rewrite existing vendor prefilled data */
  /* We only ever prefill first line */
  if (billForm.billLines && billFormUpdated.vendor) {
    billForm.billLines[0].lockedByVendor = false
  }

  const billFormAttachmentDataVendor = billFormUpdated.vendor
    ? await getBillFormFromVendorData({
        accounts,
        billForm: billFormUpdated,
        locale,
        organizationId,
        organizationCountryCode: countryId || 'DK',
        taxRates,
        vendor: billFormUpdated.vendor,
      })
    : undefined

  if (billFormAttachmentDataVendor) {
    billFormUpdated = autoCompleteBillForm({
      billFormDefault: billFormUpdated,
      billFormOverrides: billFormAttachmentDataVendor,
      baseCurrencyId,
      onlyFirstBillLine: false,
    })

    /* If new vendor data has been autofilled, we want to lock it, as vendor data takes priority over other prefills */
    /* We only ever prefill first line */
    if (billFormUpdated.billLines) {
      billFormUpdated.billLines[0].lockedByVendor = true
    }
  }

  return billFormUpdated
}

export const useAttachmentDataAutoComplete = () => {
  const { organization } = useUserOrganization()
  const { getValues, reset: resetForm, setValue } = useFormContext<BillFormSchema>()
  const locale = getCurrentLocale()
  const { accounts = [] } = useAccounts()
  const { taxRates = [] } = useTaxRates()

  const autoCompleteWithAttachmentData = useCallback(
    async (attachmentFile?: AttachmentFile) => {
      if (!organization?.id) {
        return
      }

      const billForm = getValues()

      const billFormUpdated = await getAutoCompletedFormWithAttachmentData({
        accounts,
        attachmentFile,
        baseCurrencyId: organization.baseCurrencyId,
        billForm,
        chartOfAccountId: organization?.chartOfAccountId,
        countryId: organization?.countryId,
        locale,
        organizationId: organization.id,
        taxRates,
      })

      resetForm(billFormUpdated, { keepDefaultValues: true })

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })
    },
    [
      organization?.id,
      organization?.baseCurrencyId,
      organization?.chartOfAccountId,
      organization?.countryId,
      getValues,
      accounts,
      locale,
      taxRates,
      resetForm,
      setValue,
    ],
  )

  const resetAutoCompletedWithAttachmentData = useCallback(() => {
    const billForm = getValues()

    const billFormUpdated = resetAutoCompletedBillForm(billForm, organization?.baseCurrencyId)

    resetForm(billFormUpdated, { keepDefaultValues: true })

    // Wait one frame before setting autoCompletedFields
    window.requestAnimationFrame(() => {
      setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
    })
  }, [getValues, setValue, resetForm, organization?.baseCurrencyId])

  return { autoCompleteWithAttachmentData, resetAutoCompletedWithAttachmentData }
}
